import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';

/**
 * Multi-select dropdown for short lists (< ~20 items).
 *
 * value = [] means "all selected" (no filter applied).
 * When items are toggled, value contains the subset of selected values.
 *
 * Variants:
 *   pill (default) → rounded-full trigger at 44px (h-11), matching PillSelect
 *   form           → full-width rounded-full trigger matching FormSelect.
 *                    Menu is always portaled (modal-safe).
 *
 * @example
 * <MultiPillSelect
 *   options={[{ value: 'eng', label: 'Engineering' }]}
 *   value={selectedDepts}
 *   onChange={setSelectedDepts}
 *   allLabel="All Departments"
 *   icon={Building2}
 *   iconColor="text-red-600"
 *   theme="admin"
 * />
 */
const MultiPillSelect = ({
  options = [],
  value = [],
  onChange,
  allLabel = 'All',
  icon: Icon,
  iconColor = 'text-gray-500',
  theme = 'admin',
  variant = 'pill',
  minWidth = 'min-w-[160px]',
  disabled = false,
  className = '',
  renderItem,
}) => {
  const isAdmin = theme === 'admin';
  const isPill = variant === 'pill';
  const isFormVariant = variant === 'form';
  const usePortal = isFormVariant;
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  // Pill variant: single-ref click-outside. Form/portaled: custom handler below.
  const dropdownRef = useClickOutside(() => setIsOpen(false), isOpen && !usePortal);
  const containerRef = useRef(null);

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = 288; // max-h-72
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
    const width = Math.min(rect.width, viewportWidth - 16);
    const left = Math.max(8, Math.min(rect.left, viewportWidth - width - 8));

    setDropdownStyle({
      position: 'fixed',
      left,
      width,
      zIndex: 10002,
      ...(openUpward
        ? { bottom: viewportHeight - rect.top + 4, maxHeight: Math.min(spaceAbove, dropdownMaxHeight) }
        : { top: rect.bottom + 4, maxHeight: Math.min(spaceBelow, dropdownMaxHeight) }),
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !usePortal) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, usePortal]);

  useEffect(() => {
    if (!isOpen || !usePortal) return undefined;
    updateDropdownPosition();
    const update = () => updateDropdownPosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, usePortal, updateDropdownPosition]);

  const allSelected = value.length === 0;

  const label = useMemo(() => {
    if (allSelected) return allLabel;
    if (value.length === 1) {
      const match = options.find(o => String(o.value) === String(value[0]));
      return match ? match.label : String(value[0]);
    }
    return `${value.length} selected`;
  }, [allSelected, allLabel, value, options]);

  const toggleItem = useCallback((itemValue) => {
    onChange(prev => {
      const strVal = String(itemValue);
      if (prev.map(String).includes(strVal)) {
        return prev.filter(v => String(v) !== strVal);
      }
      return [...prev, itemValue];
    });
  }, [onChange]);

  const selectAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const focusRing = isAdmin
    ? 'focus:ring-red-500 focus:border-red-500'
    : 'focus:ring-blue-500 focus:border-blue-500';

  const checkboxColor = isAdmin
    ? 'accent-red-600 text-red-600 focus-visible:ring-red-500'
    : 'accent-blue-700 text-blue-700 focus-visible:ring-blue-500';

  const isFullWidth = className.includes('w-full');

  const triggerClasses = isPill
    ? `flex items-center gap-1.5 bg-white border border-gray-200 h-11 px-4 rounded-full shadow-sm hover:border-gray-300 transition-colors ${minWidth} focus:outline-none focus:ring-2 ${focusRing} text-sm font-medium text-gray-900 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`
    : `flex items-center justify-between w-full h-11 px-4 border border-gray-300 rounded-full shadow-sm bg-white text-left focus:outline-none focus:ring-2 ${focusRing} transition-[border-color,box-shadow] duration-200 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'} ${className}`;

  const menuBody = (
    <>
      <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={selectAll}
          className={`rounded border-gray-300 ${checkboxColor}`}
        />
        <span className="text-sm font-medium text-gray-900">{allLabel}</span>
      </label>

      {options.map(option => {
        const isChecked = value.map(String).includes(String(option.value));
        return (
          <label
            key={option.value}
            className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => toggleItem(option.value)}
              className={`rounded border-gray-300 ${checkboxColor}`}
            />
            {renderItem ? renderItem(option, isChecked) : (
              <span className="text-sm text-gray-700">{option.label}</span>
            )}
          </label>
        );
      })}

      {options.length === 0 && (
        <div className="px-3 py-4 text-sm text-gray-400 text-center">No options</div>
      )}
    </>
  );

  const inlineMenu = isOpen && !usePortal && (
    <div className={`absolute ${isPill ? 'left-0 min-w-full' : 'left-0 right-0'} z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto`}>
      {menuBody}
    </div>
  );

  const portaledMenu = isOpen && usePortal && typeof document !== 'undefined' && createPortal(
    <div
      ref={menuRef}
      role="listbox"
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-y-auto"
    >
      {menuBody}
    </div>,
    document.body,
  );

  return (
    <div
      className={`relative ${isFullWidth && isPill ? 'w-full' : ''}`}
      ref={usePortal ? containerRef : dropdownRef}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={triggerClasses}
      >
        {isPill && Icon && <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />}
        {!isPill && Icon && <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0 mr-2`} />}
        <span className={`truncate ${isPill ? 'flex-1 text-left' : 'flex-1'}`}>
          {label}
        </span>
        <ChevronDown
          className={`${isPill ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-gray-500 flex-shrink-0 ${isPill ? '' : 'ml-2'} transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {inlineMenu}
      {portaledMenu}
    </div>
  );
};

export default MultiPillSelect;
